/**
 * Jaarrekening - 12 pagina's printbare jaarrekening
 * EXACT zoals Morris jaarrekening.html
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BoekhoudingLayout from '../../components/boekhouding/BoekhoudingLayout';
import styles from '../../styles/Boekhouding.module.css';
import { boekhoudingService, JaarrekeningData } from '../../services/boekhouding.service';

const Jaarrekening = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<JaarrekeningData | null>(null);

  // Huidig boekjaar uit localStorage
  const huidigJaar = parseInt(localStorage.getItem('boekhouding_jaar') || new Date().getFullYear().toString());

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const result = await boekhoudingService.getJaarrekening(huidigJaar);
        setData(result);
      } catch (err) {
        console.error('Error loading jaarrekening:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [huidigJaar]);

  // Format bedrag - EXACT Morris bedrag_heel filter
  const formatBedrag = (bedrag: number | undefined | null): string => {
    if (bedrag === undefined || bedrag === null || bedrag === 0) return '';
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Math.round(bedrag)).replace('€', '€ ');
  };

  // Print functie
  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <BoekhoudingLayout title={`Jaarrekening ${huidigJaar}`}>
        <div className={styles.loading}>Laden...</div>
      </BoekhoudingLayout>
    );
  }

  if (!data) {
    return (
      <BoekhoudingLayout title={`Jaarrekening ${huidigJaar}`}>
        <div className={styles.error}>Kon jaarrekening data niet laden</div>
      </BoekhoudingLayout>
    );
  }

  // Bereken totalen voor exploitatie - EXACT Morris
  const omzetTotaal = data.exploitatie.omzet.totaal;
  const kostenTotaal = data.exploitatie.kosten_totaal;
  const winstTotaal = data.exploitatie.winst;

  // Bereken kosten per categorie - EXACT Morris
  const getKostTotaal = (naam: string): number => {
    const kost = data.exploitatie.kosten.find(k => k.naam === naam);
    return kost ? kost.totaal : 0;
  };

  const materiaal = getKostTotaal('Materiaal');
  const inkoopGoederen = getKostTotaal('Inkoop goederen');
  const autokosten = getKostTotaal('Autokosten') + getKostTotaal('Privégebruik auto');
  const afschrijvingen = getKostTotaal('Afschrijvingen');

  // Verkoopkosten: alle kosten behalve specifieke
  const verkoopKostenNamen = ['Bankkosten', 'Verzekeringen', 'Gereedschap/materiaal', 'Kantoorkosten', 'Reclame/marketing', 'Telefoon/internet', 'Uitbesteed'];
  const verkoopkosten = data.exploitatie.kosten
    .filter(k => verkoopKostenNamen.includes(k.naam))
    .reduce((sum, k) => sum + k.totaal, 0);

  const andereKosten = getKostTotaal('Onkosten');

  const inkoopkostenTotaal = materiaal + inkoopGoederen;
  const overigeKostenTotaal = autokosten + verkoopkosten + andereKosten + afschrijvingen;

  // Balans berekeningen - reken met ruwe waarden, rond alleen totalen af
  const eindBtw = data.balans.activa.btw_vordering || 0;
  const eindDeb = data.balans.activa.debiteuren || 0;
  const eindVasteActiva = data.balans.activa.vaste_activa || {};
  const eindVasteActivaTotaal = Object.values(eindVasteActiva).reduce((sum: number, v: any) => sum + (Number(v) || 0), 0);
  const banknaam = data.balans.banknaam || 'Bank';
  const eindBank = data.balans.activa.bank || 0;
  const eindKas = data.balans.activa.kas || 0;
  const eindSubtotaal = eindBtw + eindDeb + eindVasteActivaTotaal;
  const eindLiquide = eindBank + eindKas;
  const eindTotaal = Math.round(eindSubtotaal + eindLiquide);

  // Beginbalans activa - dynamisch vaste activa ophalen
  const beginBtw = data.beginbalans.activa['Omzetbelasting'] || 0;
  const beginDeb = data.beginbalans.activa['Debiteuren'] || 0;
  // Vaste activa uit beginbalans: alles behalve bekende niet-vaste-activa posten
  const beginVasteActivaNamen = Object.keys(data.beginbalans.activa).filter(
    k => !['Omzetbelasting', 'Debiteuren', banknaam, 'Bank', 'Rabobank', 'Kas'].includes(k)
  );
  const beginVasteActivaTotaal = beginVasteActivaNamen.reduce(
    (sum, k) => sum + (Number(data.beginbalans.activa[k]) || 0), 0
  );
  const beginBank = data.beginbalans.activa[banknaam] ?? data.beginbalans.activa['Bank'] ?? data.beginbalans.activa['Rabobank'] ?? 0;
  const beginKas = data.beginbalans.activa['Kas'] || 0;
  const beginSubtotaal = beginBtw + beginDeb + beginVasteActivaTotaal;
  const beginLiquide = beginBank + beginKas;
  const beginTotaal = Math.round(beginSubtotaal + beginLiquide);

  // Alle vaste activa namen (eind + begin gecombineerd)
  const vasteActivaNamen = [...new Set([
    ...Object.keys(eindVasteActiva),
    ...beginVasteActivaNamen,
  ])];

  // Passiva eind
  const eindKapitaal = data.balans.passiva.kapitaal || 0;
  const eindOr = data.balans.passiva.oudedagreserve || 0;
  const eindOndernemingsvermogen = eindKapitaal + eindOr;
  const eindLening = data.balans.passiva.lening || 0;
  const eindCrediteuren = data.balans.passiva.crediteuren || 0;
  const eindPriveKm = data.balans.passiva.prive_km || 0;
  const eindBtwSchuld = data.balans.passiva.btw_schuld || 0;
  const eindBtwAuto = data.balans.passiva.btw_prive_auto || 0;
  const eindKortlopend = eindCrediteuren + eindPriveKm + eindBtwSchuld + eindBtwAuto;
  const eindPassivaTotaal = Math.round(eindOndernemingsvermogen + eindLening + eindKortlopend);

  // Passiva begin
  const beginKapitaal = data.beginbalans.passiva['Kapitaal'] || 0;
  const beginOr = data.beginbalans.passiva['Oudedagsreserve'] || data.beginbalans.passiva['Oudedagreserve'] || 0;
  const beginOndernemingsvermogen = beginKapitaal + beginOr;
  const beginLening = data.beginbalans.passiva['Lening Ford Transit'] || data.beginbalans.passiva['Lening'] || 0;
  const beginCrediteuren = data.beginbalans.passiva['Crediteuren'] || 0;
  const beginBtwSchuld = data.beginbalans.passiva['Omzetbelasting'] || data.beginbalans.passiva['BTW schuld'] || 0;
  const beginBtwAuto = data.beginbalans.passiva['Extra BTW auto'] || 0;
  const beginKortlopend = beginCrediteuren + beginBtwSchuld + beginBtwAuto;
  const beginPassivaTotaal = Math.round(beginOndernemingsvermogen + beginLening + beginKortlopend);

  // Controles
  const activaBerekend = eindBtw + eindDeb + eindBank + eindKas + eindVasteActivaTotaal;
  const passivaBerekend = eindKapitaal + eindOr + eindLening + eindCrediteuren + eindPriveKm + eindBtwSchuld + eindBtwAuto;
  const balansVerschil = activaBerekend - passivaBerekend;

  const afschrExploitatie = afschrijvingen;
  const afschrDetail = data.afschrijvingen.totaal.afschrijving;
  const afschrVerschil = afschrExploitatie - afschrDetail;

  const vasteActivaBalans = eindVasteActivaTotaal;
  const vasteActivaDetail = data.afschrijvingen.totaal.eind_boekwaarde;
  const vasteActivaVerschil = vasteActivaBalans - vasteActivaDetail;

  const kapitaalVerschil = data.kapitaal.eigen_vermogen - data.balans.passiva.kapitaal;

  // Groepeer activa per categorie voor afschrijvingen
  const categorieVolgorde = Object.keys(data.afschrijvingen.per_categorie);

  return (
    <BoekhoudingLayout title={`Jaarrekening ${huidigJaar}`}>
      <div className={styles.jaarrekening}>
        {/* Print knoppen - EXACT Morris */}
        <div className={styles.printButtons}>
        <button className={styles.btnBack} onClick={() => navigate('/boekhouding')}>Terug</button>
        <button className={styles.btnPrint} onClick={handlePrint}>Printen</button>
      </div>

      {/* Pagina 1: Voorblad - EXACT Morris */}
      <div className={styles.page}>
        <div className={styles.voorblad}>
          <h1>{data.bedrijf.naam_regel1}</h1>
          <h2>{data.bedrijf.naam_regel2}</h2>
          <div className={styles.jaarrekeningTitel}>JAARREKENING</div>
          <div className={styles.jaarNummer}>{data.jaar}</div>
          <div className={styles.adresBalk}>
            <div className={styles.adresTekst}>
              {data.bedrijf.naam}<br />
              {data.bedrijf.adres}<br />
              {data.bedrijf.postcode} {data.bedrijf.plaats}
            </div>
            <div className={styles.cirkel}></div>
          </div>
        </div>
      </div>

      {/* Pagina 2: Inhoudsopgave - EXACT Morris */}
      <div className={styles.page}>
        <div className={styles.sectionHeaderBar}>Inhoudsopgave</div>
        <table className={styles.tocTable}>
          <tbody>
            <tr><td colSpan={2} style={{fontWeight: 'bold', paddingBottom: '0.5rem', textAlign: 'left'}}>Jaarrekening</td></tr>
            <tr><td>Voorblad</td><td>1</td></tr>
            <tr><td>Inhoudsopgave</td><td>2</td></tr>
            <tr><td>Balans {data.jaar}</td><td>3</td></tr>
            <tr><td>Winst en verliesrekening {data.jaar}</td><td>4</td></tr>
            <tr><td>Afschrijvingen</td><td>5</td></tr>
            <tr><td>Privé ontrekkingen en stortingen {data.jaar}</td><td>6</td></tr>
            <tr><td>Fiscale winstberekening {data.jaar}</td><td>7</td></tr>
            <tr><td>Controles {data.jaar}</td><td>8</td></tr>
            <tr><td>Kapitaalsvergelijking {data.jaar}</td><td>9</td></tr>
            <tr><td>Exploitatierekening uitgebreid {data.jaar}</td><td>10</td></tr>
            <tr><td>BTW Overzicht {data.jaar}</td><td>11</td></tr>
          </tbody>
        </table>
        <div className={styles.pageFooter}>
          <span>Jaarrekening {data.bedrijf.naam} {data.jaar}</span>
          <span>Pagina 2 van 11</span>
        </div>
      </div>

      {/* Pagina 3: Balans - EXACT Morris */}
      <div className={styles.page}>
        <div className={styles.sectionHeaderBar}>Balans Activa {data.jaar}</div>
        <table className={styles.balansTable}>
          <thead>
            <tr>
              <th className={styles.colLabel}></th>
              <th className={styles.colValue}>Begin</th>
              <th className={styles.colValue}>Eind</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className={styles.colLabel}><strong>Vorderingen</strong></td>
              <td className={styles.colValue}></td>
              <td className={styles.colValue}></td>
            </tr>
            <tr>
              <td className={`${styles.colLabel} ${styles.indent}`}>Omzetbelasting</td>
              <td className={styles.colValue}>{beginBtw ? formatBedrag(beginBtw) : ''}</td>
              <td className={styles.colValue}>{eindBtw ? formatBedrag(eindBtw) : ''}</td>
            </tr>
            <tr>
              <td className={`${styles.colLabel} ${styles.indent}`}>Vorderingen op debiteuren</td>
              <td className={styles.colValue}>{formatBedrag(beginDeb)}</td>
              <td className={styles.colValue}>{formatBedrag(eindDeb)}</td>
            </tr>
            <tr><td></td><td></td><td></td></tr>
            {vasteActivaNamen.map(naam => {
              const beginVal = Number(data.beginbalans.activa[naam]) || 0;
              const eindVal = Number(eindVasteActiva[naam]) || 0;
              if (!beginVal && !eindVal) return null;
              return (
                <tr key={naam}>
                  <td className={`${styles.colLabel} ${styles.indent}`}>{naam}</td>
                  <td className={styles.colValue}>{beginVal ? formatBedrag(beginVal) : ''}</td>
                  <td className={styles.colValue}>{eindVal ? formatBedrag(eindVal) : ''}</td>
                </tr>
              );
            })}
            <tr>
              <td></td>
              <td className={styles.colValue}>========</td>
              <td className={styles.colValue}>========</td>
            </tr>
            <tr>
              <td className={`${styles.colLabel} ${styles.indent}`}><em>Subtotaal</em></td>
              <td className={styles.colValue}>{formatBedrag(beginSubtotaal)}</td>
              <td className={styles.colValue}>{formatBedrag(eindSubtotaal)}</td>
            </tr>
            <tr><td></td><td></td><td></td></tr>
            <tr>
              <td className={styles.colLabel}>Liquide middelen</td>
              <td className={styles.colValue}>{formatBedrag(beginLiquide)}</td>
              <td className={styles.colValue}>{formatBedrag(eindLiquide)}</td>
            </tr>
            <tr><td></td><td></td><td></td></tr>
            <tr>
              <td></td>
              <td className={styles.colValue}>========</td>
              <td className={styles.colValue}>========</td>
            </tr>
            <tr>
              <td className={`${styles.colLabel} ${styles.indent}`}><strong>Totaal Activa</strong></td>
              <td className={styles.colValue}><strong>{formatBedrag(beginTotaal)}</strong></td>
              <td className={styles.colValue}><strong>{formatBedrag(eindTotaal)}</strong></td>
            </tr>
          </tbody>
        </table>

        <div className={styles.sectionHeaderBar} style={{marginTop: '1.5rem'}}>Balans Pasiva {data.jaar}</div>
        <table className={styles.balansTable}>
          <thead>
            <tr>
              <th className={styles.colLabel}></th>
              <th className={styles.colValue}>Begin</th>
              <th className={styles.colValue}>Eind</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className={styles.colLabel}>Ondernemings vermogen</td>
              <td className={styles.colValue}></td>
              <td className={styles.colValue}></td>
            </tr>
            <tr>
              <td className={`${styles.colLabel} ${styles.indent}`}>Eigen vermogen</td>
              <td className={styles.colValue}>{formatBedrag(beginKapitaal)}</td>
              <td className={styles.colValue}>{formatBedrag(eindKapitaal)}</td>
            </tr>
            <tr>
              <td className={`${styles.colLabel} ${styles.indent}`}>Oudedagsreserve</td>
              <td className={styles.colValue}>{formatBedrag(beginOr)}</td>
              <td className={styles.colValue}>{formatBedrag(eindOr)}</td>
            </tr>
            <tr>
              <td></td>
              <td className={styles.colValue}>========</td>
              <td className={styles.colValue}>========</td>
            </tr>
            <tr>
              <td></td>
              <td className={styles.colValue}>{formatBedrag(beginOndernemingsvermogen)}</td>
              <td className={styles.colValue}>{formatBedrag(eindOndernemingsvermogen)}</td>
            </tr>
            <tr><td></td><td></td><td></td></tr>
            <tr>
              <td className={styles.colLabel}>Langlopende schulden</td>
              <td className={styles.colValue}></td>
              <td className={styles.colValue}></td>
            </tr>
            <tr>
              <td className={`${styles.colLabel} ${styles.indent}`}>Lening</td>
              <td className={styles.colValue}>{beginLening ? formatBedrag(beginLening) : ''}</td>
              <td className={styles.colValue}>{eindLening ? formatBedrag(eindLening) : ''}</td>
            </tr>
            <tr>
              <td></td>
              <td className={styles.colValue}>========</td>
              <td className={styles.colValue}>========</td>
            </tr>
            <tr>
              <td className={`${styles.colLabel} ${styles.indent}`}>Totaal langlopende schulden</td>
              <td className={styles.colValue}>{beginLening ? formatBedrag(beginLening) : '€ 0'}</td>
              <td className={styles.colValue}>{formatBedrag(eindLening)}</td>
            </tr>
            <tr><td></td><td></td><td></td></tr>
            <tr>
              <td className={styles.colLabel}>Kortlopende schulden</td>
              <td className={styles.colValue}></td>
              <td className={styles.colValue}></td>
            </tr>
            <tr>
              <td className={`${styles.colLabel} ${styles.indent}`}>Schulden aan crediteuren</td>
              <td className={styles.colValue}>{formatBedrag(beginCrediteuren)}</td>
              <td className={styles.colValue}>{formatBedrag(eindCrediteuren)}</td>
            </tr>
            {eindPriveKm > 0 && (
            <tr>
              <td className={`${styles.colLabel} ${styles.indent}`}>Autokosten</td>
              <td className={styles.colValue}></td>
              <td className={styles.colValue}>{formatBedrag(eindPriveKm)}</td>
            </tr>
            )}
            <tr>
              <td className={`${styles.colLabel} ${styles.indent}`}>Schuld omzetbelasting</td>
              <td className={styles.colValue}>{formatBedrag(beginBtwSchuld)}</td>
              <td className={styles.colValue}>{formatBedrag(eindBtwSchuld)}</td>
            </tr>
            {(eindBtwAuto > 0 || beginBtwAuto > 0) && (
              <tr>
                <td className={`${styles.colLabel} ${styles.indent}`}>BTW privé gebruik auto</td>
                <td className={styles.colValue}>{beginBtwAuto ? formatBedrag(beginBtwAuto) : ''}</td>
                <td className={styles.colValue}>{formatBedrag(eindBtwAuto)}</td>
              </tr>
            )}
            <tr>
              <td></td>
              <td className={styles.colValue}>========</td>
              <td className={styles.colValue}>========</td>
            </tr>
            <tr>
              <td className={`${styles.colLabel} ${styles.indent}`}>Totaal kortlopende schulden</td>
              <td className={styles.colValue}>{formatBedrag(beginKortlopend)}</td>
              <td className={styles.colValue}>{formatBedrag(eindKortlopend)}</td>
            </tr>
            <tr><td></td><td></td><td></td></tr>
            <tr>
              <td></td>
              <td className={styles.colValue}>========</td>
              <td className={styles.colValue}>========</td>
            </tr>
            <tr>
              <td className={styles.colLabel}><strong>Totaal Pasiva</strong></td>
              <td className={styles.colValue}><strong>{formatBedrag(beginPassivaTotaal)}</strong></td>
              <td className={styles.colValue}><strong>{formatBedrag(eindPassivaTotaal)}</strong></td>
            </tr>
          </tbody>
        </table>
        {Math.round(balansVerschil) !== 0 && (
          <div className={styles.controleWaarschuwing}>
            LET OP: Balans is niet in evenwicht! Activa ({formatBedrag(activaBerekend)}) ≠ Passiva ({formatBedrag(passivaBerekend)}). Verschil: {formatBedrag(balansVerschil)}
          </div>
        )}
        <div className={styles.pageFooter}>
          <span>Jaarrekening {data.bedrijf.naam} {data.jaar}</span>
          <span>Pagina 3 van 11</span>
        </div>
      </div>

      {/* Pagina 4: Winst & Verliesrekening - EXACT Morris */}
      <div className={styles.page}>
        <div className={styles.sectionHeaderBar}>Winst en verlies rekening {data.jaar}</div>

        <table className={styles.jrTable}>
          <tbody>
            <tr>
              <td style={{width: '55%'}}><strong>Opbrengsten</strong></td>
              <td className={styles.alignRight} style={{width: '20%'}}></td>
              <td className={styles.alignRight} style={{width: '25%'}}></td>
            </tr>
            <tr>
              <td className={styles.indent}>Opbrengsten uit leveringen en diensten</td>
              <td className={styles.alignRight}></td>
              <td className={styles.alignRight}>{formatBedrag(omzetTotaal)}</td>
            </tr>
          </tbody>
        </table>

        <table className={styles.jrTable} style={{marginTop: '1rem'}}>
          <tbody>
            <tr>
              <td style={{width: '55%'}}><strong>Inkoopkosten</strong></td>
              <td className={styles.alignRight} style={{width: '20%'}}></td>
              <td className={styles.alignRight} style={{width: '25%'}}></td>
            </tr>
            <tr>
              <td className={styles.indent}>Materiaal</td>
              <td className={styles.alignRight}>{formatBedrag(materiaal)}</td>
              <td className={styles.alignRight}></td>
            </tr>
            <tr>
              <td className={styles.indent}>Inkoop goederen</td>
              <td className={styles.alignRight}>{formatBedrag(inkoopGoederen)}</td>
              <td className={styles.alignRight}></td>
            </tr>
          </tbody>
        </table>

        <table className={styles.jrTable} style={{marginTop: '1rem'}}>
          <tbody>
            <tr>
              <td style={{width: '55%'}}><strong>Overige bedrijfskosten</strong></td>
              <td className={styles.alignRight} style={{width: '20%'}}></td>
              <td className={styles.alignRight} style={{width: '25%'}}></td>
            </tr>
            <tr>
              <td className={styles.indent}>Auto en transport kosten</td>
              <td className={styles.alignRight}>{formatBedrag(autokosten)}</td>
              <td className={styles.alignRight}></td>
            </tr>
            <tr>
              <td className={styles.indent}>Verkoopkosten</td>
              <td className={styles.alignRight}>{formatBedrag(verkoopkosten)}</td>
              <td className={styles.alignRight}></td>
            </tr>
            <tr>
              <td className={styles.indent}>Andere Kosten</td>
              <td className={styles.alignRight}>{formatBedrag(andereKosten)}</td>
              <td className={styles.alignRight}></td>
            </tr>
            <tr>
              <td className={styles.indent}>Afschrijvingen</td>
              <td className={styles.alignRight}>{formatBedrag(afschrijvingen)}</td>
              <td className={styles.alignRight}></td>
            </tr>
            <tr><td></td><td></td><td></td></tr>
            <tr>
              <td></td>
              <td className={styles.alignRight}>========</td>
              <td className={styles.alignRight}>========</td>
            </tr>
            <tr>
              <td></td>
              <td className={styles.alignRight}>{formatBedrag(inkoopkostenTotaal + overigeKostenTotaal)}</td>
              <td className={styles.alignRight}>{formatBedrag(omzetTotaal)}</td>
            </tr>
          </tbody>
        </table>

        <table className={styles.jrTable} style={{marginTop: '1rem'}}>
          <tbody>
            <tr>
              <td style={{width: '55%'}} className={styles.indent}>Subtotaal</td>
              <td className={styles.alignRight} style={{width: '20%'}}></td>
              <td className={styles.alignRight} style={{width: '25%'}}>{formatBedrag(winstTotaal)}</td>
            </tr>
          </tbody>
        </table>

        <table className={styles.jrTable} style={{marginTop: '1rem'}}>
          <tbody>
            <tr>
              <td style={{width: '55%'}}><strong>Financiele baten en lasten</strong></td>
              <td className={styles.alignRight} style={{width: '20%'}}></td>
              <td className={styles.alignRight} style={{width: '25%'}}></td>
            </tr>
            <tr>
              <td className={styles.indent}>Opbrengsten banktegoeden (Rente)</td>
              <td className={styles.alignRight}>0,00</td>
              <td className={styles.alignRight}></td>
            </tr>
            <tr>
              <td></td>
              <td className={styles.alignRight}>========</td>
              <td className={styles.alignRight}></td>
            </tr>
            <tr>
              <td></td>
              <td className={styles.alignRight}>0,00</td>
              <td className={styles.alignRight}>0,00</td>
            </tr>
            <tr>
              <td></td>
              <td className={styles.alignRight}></td>
              <td className={styles.alignRight}>========</td>
            </tr>
          </tbody>
        </table>

        <table className={styles.jrTable} style={{marginTop: '0.5rem'}}>
          <tbody>
            <tr>
              <td style={{width: '55%'}} className={styles.indent}>resultaat uit gewone bedrijfsuitvoering</td>
              <td className={styles.alignRight} style={{width: '20%'}}></td>
              <td className={styles.alignRight} style={{width: '25%'}}><strong>{formatBedrag(winstTotaal)}</strong></td>
            </tr>
          </tbody>
        </table>

        {Math.round(afschrVerschil) !== 0 && (
          <div className={styles.controleWaarschuwing}>
            LET OP: Afschrijvingen ({formatBedrag(afschrExploitatie)}) ≠ Afschrijvingen pagina 5 ({formatBedrag(afschrDetail)})
          </div>
        )}
        <div className={styles.pageFooter}>
          <span>Jaarrekening {data.bedrijf.naam} {data.jaar}</span>
          <span>Pagina 4 van 11</span>
        </div>
      </div>

      {/* Pagina 5: Afschrijvingen - EXACT Morris */}
      <div className={styles.page}>
        <div className={styles.sectionHeaderBar}>Afschrijvingen</div>

        <table className={styles.jrTable} style={{marginTop: '0.5rem', fontSize: '9pt'}}>
          <thead>
            <tr>
              <th style={{width: '22%'}}></th>
              <th style={{width: '10%'}}></th>
              <th className={styles.alignRight} style={{width: '12%'}}>afgeschreven</th>
              <th className={styles.alignRight} style={{width: '14%'}}>1-1-{data.jaar}</th>
              <th className={styles.alignRight} style={{width: '12%'}}>afschrijven</th>
              <th className={styles.alignRight} style={{width: '14%'}}>31-12-{data.jaar}</th>
            </tr>
          </thead>
        </table>

        {categorieVolgorde.map(cat => {
          const catItems = data.afschrijvingen.activa.filter(i => i.categorie === cat);
          if (catItems.length === 0) return null;
          return (
            <div key={cat}>
              <div style={{marginTop: '0', padding: '2px 0', fontWeight: 'bold', fontSize: '11pt', textDecoration: 'underline'}}>{cat}</div>
              <table className={styles.jrTable} style={{fontSize: '9pt'}}>
                <tbody>
                  {catItems.map(item => (
                    <tr key={item.id}>
                      <td style={{width: '22%'}}><strong>{item.omschrijving}</strong></td>
                      <td style={{width: '10%'}}>{item.datum}</td>
                      <td className={styles.alignRight} style={{width: '12%'}}>{formatBedrag(item.aanschafwaarde)}</td>
                      <td className={styles.alignRight} style={{width: '14%'}}>{formatBedrag(item.afgeschreven)}</td>
                      <td className={styles.alignRight} style={{width: '12%'}}>{formatBedrag(item.begin_boekwaarde)}</td>
                      <td className={styles.alignRight} style={{width: '14%'}}>{formatBedrag(item.afschrijving)}</td>
                      <td className={styles.alignRight} style={{width: '14%'}}>{formatBedrag(item.eind_boekwaarde)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })}

        <div className={styles.sectionHeaderBar} style={{marginTop: '2.5rem'}}>Afschrijvings totalen</div>
        <table className={styles.jrTable} style={{marginTop: '0.5rem', fontSize: '9pt'}}>
          <thead>
            <tr>
              <th style={{width: '30%'}}></th>
              <th className={styles.alignRight} style={{width: '20%'}}>1-1-{data.jaar}</th>
              <th className={styles.alignRight} style={{width: '20%'}}>31-12-{data.jaar}</th>
              <th className={styles.alignRight} style={{width: '20%'}}>Totaal {data.jaar}</th>
            </tr>
          </thead>
          <tbody>
            {categorieVolgorde.map(cat => {
              const catData = data.afschrijvingen.per_categorie[cat];
              if (!catData) return null;
              return (
                <tr key={cat}>
                  <td style={{width: '30%'}}><strong>{cat}</strong></td>
                  <td className={styles.alignRight} style={{width: '20%'}}>{formatBedrag(catData.begin_boekwaarde)}</td>
                  <td className={styles.alignRight} style={{width: '20%'}}>{formatBedrag(catData.eind_boekwaarde)}</td>
                  <td className={styles.alignRight} style={{width: '20%'}}>{formatBedrag(catData.afschrijving)}</td>
                </tr>
              );
            })}
            <tr>
              <td></td>
              <td></td>
              <td></td>
              <td className={styles.alignRight}>========</td>
            </tr>
            <tr>
              <td></td>
              <td></td>
              <td className={styles.alignRight}><strong>Totaal afschrijvingen</strong></td>
              <td className={styles.alignRight}><strong>{formatBedrag(data.afschrijvingen.totaal.afschrijving)}</strong></td>
            </tr>
          </tbody>
        </table>

        <div className={styles.pageFooter}>
          <span>Jaarrekening {data.bedrijf.naam} {data.jaar}</span>
          <span>Pagina 5 van 11</span>
        </div>
      </div>

      {/* Pagina 6: Privé ontrekkingen - EXACT Morris */}
      <div className={styles.page}>
        <div className={styles.sectionHeaderBar}>Prive ontrekkingen en stortingen {data.jaar}</div>

        <table className={styles.jrTable} style={{marginTop: '1.5rem'}}>
          <tbody>
            <tr>
              <td><strong>Privé ontrekkingen</strong></td>
              <td className={styles.alignRight}></td>
            </tr>
            <tr>
              <td className={styles.indent}>In geld</td>
              <td className={styles.alignRight}>{formatBedrag(data.kapitaal.prive_opnames)}</td>
            </tr>
            <tr><td></td><td></td></tr>
            <tr>
              <td><strong>Privé stortingen</strong></td>
              <td className={styles.alignRight}></td>
            </tr>
            <tr>
              <td className={styles.indent}>In geld</td>
              <td className={styles.alignRight}>{formatBedrag(data.kapitaal.prive_stortingen || 0)}</td>
            </tr>
            <tr>
              <td></td>
              <td className={styles.alignRight}>========</td>
            </tr>
            <tr>
              <td></td>
              <td className={styles.alignRight}>{formatBedrag(data.kapitaal.prive_opnames - (data.kapitaal.prive_stortingen || 0))}</td>
            </tr>
          </tbody>
        </table>

        <div className={styles.pageFooter}>
          <span>Jaarrekening {data.bedrijf.naam} {data.jaar}</span>
          <span>Pagina 6 van 11</span>
        </div>
      </div>

      {/* Pagina 7: Fiscale winstberekening - EXACT Morris */}
      <div className={styles.page}>
        <div className={styles.sectionHeaderBar}>Fiscale winstberekening {data.jaar}</div>

        <table className={styles.jrTable} style={{marginTop: '1.5rem'}}>
          <tbody>
            <tr>
              <td className={styles.indent}>Ondernemingsvermogen einde boekjaar</td>
              <td className={styles.alignRight}>{formatBedrag(data.kapitaal.ondernemingsvermogen)}</td>
            </tr>
            <tr>
              <td className={styles.indent}>Ondernemingsvermogen begin boekjaar</td>
              <td className={styles.alignRight}>{formatBedrag(data.kapitaal.begin_saldo + data.kapitaal.or_begin)}</td>
            </tr>
            <tr>
              <td></td>
              <td className={styles.alignRight}>========</td>
            </tr>
            <tr>
              <td className={styles.indent}>Saldo ondernemingsvermogen</td>
              <td className={styles.alignRight}><strong>{formatBedrag(data.kapitaal.ondernemingsvermogen - data.kapitaal.begin_saldo - data.kapitaal.or_begin)}</strong></td>
            </tr>
            <tr><td></td><td></td></tr>
            <tr>
              <td className={styles.indent}>Priveontrekkingen (incl. OR dotatie)</td>
              <td className={styles.alignRight}>{formatBedrag(data.kapitaal.totaal_onttrekkingen)}</td>
            </tr>
            <tr>
              <td></td>
              <td className={styles.alignRight}>========</td>
            </tr>
            <tr>
              <td className={styles.indent}>Winstberekening</td>
              <td className={styles.alignRight}><strong>{formatBedrag(winstTotaal)}</strong></td>
            </tr>
          </tbody>
        </table>

        <div className={styles.sectionHeaderBar} style={{marginTop: '3rem', width: '40%', textAlign: 'left'}}>Controle winsberekening</div>
        <table className={styles.jrTable} style={{marginTop: '1rem'}}>
          <tbody>
            <tr>
              <td className={styles.indent}>Winst volgens winst en verliesberekening</td>
              <td className={styles.alignRight}>{formatBedrag(winstTotaal)}</td>
            </tr>
            <tr><td></td><td></td></tr>
            <tr>
              <td className={styles.indent}>Fiscale winsberekening</td>
              <td className={styles.alignRight}>{formatBedrag(winstTotaal)}</td>
            </tr>
            <tr>
              <td></td>
              <td className={styles.alignRight}>========</td>
            </tr>
            <tr>
              <td className={styles.alignRight}><strong>Verschil</strong></td>
              <td className={styles.alignRight}>{formatBedrag(0)}</td>
            </tr>
          </tbody>
        </table>

        <div className={styles.pageFooter}>
          <span>Jaarrekening {data.bedrijf.naam} {data.jaar}</span>
          <span>Pagina 7 van 11</span>
        </div>
      </div>

      {/* Pagina 8: Controles - EXACT Morris */}
      <div className={styles.page}>
        <div className={styles.sectionHeaderBar}>Controles {data.jaar}</div>

        <div className={styles.sectionHeaderBar} style={{marginTop: '1.5rem', width: '40%', textAlign: 'left'}}>Controle balansevenwicht</div>
        <table className={styles.jrTable}>
          <tbody>
            <tr>
              <td>Totaal Activa (berekend)</td>
              <td className={styles.alignRight}>{formatBedrag(activaBerekend)}</td>
            </tr>
            <tr>
              <td>Totaal Passiva (berekend)</td>
              <td className={styles.alignRight}>{formatBedrag(passivaBerekend)}</td>
            </tr>
            <tr>
              <td></td>
              <td className={styles.alignRight}>========</td>
            </tr>
            <tr>
              <td><strong>Verschil</strong></td>
              <td className={`${styles.alignRight} ${Math.round(balansVerschil) !== 0 ? styles.controleFout : ''}`}>
                <strong>{Math.abs(balansVerschil) < 1 ? '0' : formatBedrag(balansVerschil)}</strong>
              </td>
            </tr>
          </tbody>
        </table>
        {Math.round(balansVerschil) !== 0 && (
          <div className={styles.controleWaarschuwing}>
            LET OP: Balans is niet in evenwicht! Verschil: {formatBedrag(balansVerschil)}
          </div>
        )}

        <div className={styles.sectionHeaderBar} style={{marginTop: '2rem', width: '40%', textAlign: 'left'}}>Controle afschrijvingen</div>
        <table className={styles.jrTable}>
          <tbody>
            <tr>
              <td>Afschrijvingen exploitatierekening (som kwartalen)</td>
              <td className={styles.alignRight}>{formatBedrag(afschrExploitatie)}</td>
            </tr>
            <tr>
              <td>Afschrijvingen activa detail (pagina 5)</td>
              <td className={styles.alignRight}>{formatBedrag(afschrDetail)}</td>
            </tr>
            <tr>
              <td></td>
              <td className={styles.alignRight}>========</td>
            </tr>
            <tr>
              <td><strong>Verschil</strong></td>
              <td className={`${styles.alignRight} ${Math.round(afschrVerschil) !== 0 ? styles.controleFout : ''}`}>
                <strong>{Math.abs(afschrVerschil) < 1 ? '0' : formatBedrag(afschrVerschil)}</strong>
              </td>
            </tr>
          </tbody>
        </table>
        {Math.round(afschrVerschil) !== 0 && (
          <div className={styles.controleWaarschuwing}>
            LET OP: Afschrijvingen komen niet overeen! Verschil: {formatBedrag(afschrVerschil)}
          </div>
        )}

        <div className={styles.sectionHeaderBar} style={{marginTop: '2rem', width: '40%', textAlign: 'left'}}>Controle vaste activa</div>
        <table className={styles.jrTable}>
          <tbody>
            <tr>
              <td>Vaste activa balans (pagina 3)</td>
              <td className={styles.alignRight}>{formatBedrag(vasteActivaBalans)}</td>
            </tr>
            <tr>
              <td>Eind boekwaarde activa detail (pagina 5)</td>
              <td className={styles.alignRight}>{formatBedrag(vasteActivaDetail)}</td>
            </tr>
            <tr>
              <td></td>
              <td className={styles.alignRight}>========</td>
            </tr>
            <tr>
              <td><strong>Verschil</strong></td>
              <td className={`${styles.alignRight} ${Math.round(vasteActivaVerschil) !== 0 ? styles.controleFout : ''}`}>
                <strong>{Math.abs(vasteActivaVerschil) < 1 ? '0' : formatBedrag(vasteActivaVerschil)}</strong>
              </td>
            </tr>
          </tbody>
        </table>
        {Math.round(vasteActivaVerschil) !== 0 && (
          <div className={styles.controleWaarschuwing}>
            LET OP: Vaste activa komen niet overeen! Verschil: {formatBedrag(vasteActivaVerschil)}
          </div>
        )}

        <div className={styles.sectionHeaderBar} style={{marginTop: '2rem', width: '40%', textAlign: 'left'}}>Controle kapitaalsvergelijking</div>
        <table className={styles.jrTable}>
          <tbody>
            <tr>
              <td>Berekend eigen vermogen eind</td>
              <td className={styles.alignRight}>{formatBedrag(data.kapitaal.eigen_vermogen)}</td>
            </tr>
            <tr>
              <td>Balans kapitaal</td>
              <td className={styles.alignRight}>{formatBedrag(data.balans.passiva.kapitaal)}</td>
            </tr>
            <tr>
              <td></td>
              <td className={styles.alignRight}>========</td>
            </tr>
            <tr>
              <td><strong>Verschil</strong></td>
              <td className={`${styles.alignRight} ${Math.abs(kapitaalVerschil) > 1 ? styles.controleFout : ''}`}>
                <strong>{Math.abs(kapitaalVerschil) < 1 ? '0' : formatBedrag(kapitaalVerschil)}</strong>
              </td>
            </tr>
          </tbody>
        </table>
        {Math.abs(kapitaalVerschil) > 1 && (
          <div className={styles.controleWaarschuwing}>
            LET OP: Kapitaalsvergelijking komt niet overeen! Verschil: {formatBedrag(kapitaalVerschil)}
          </div>
        )}

        <div className={styles.pageFooter}>
          <span>Jaarrekening {data.bedrijf.naam} {data.jaar}</span>
          <span>Pagina 8 van 11</span>
        </div>
      </div>

      {/* Pagina 9: Kapitaalsvergelijking - EXACT Morris */}
      <div className={styles.page}>
        <div className={styles.sectionHeaderBar}>Kapitaalsvergelijking {data.jaar}</div>

        <table className={styles.jrTable} style={{marginTop: '1.5rem'}}>
          <tbody>
            <tr>
              <td className={styles.indent}>Eigen vermogen 1-1-{data.jaar}</td>
              <td className={styles.alignRight}>{formatBedrag(data.kapitaal.begin_saldo)}</td>
            </tr>
            <tr>
              <td className={styles.indent}>Winst</td>
              <td className={styles.alignRight}>{formatBedrag(winstTotaal)}</td>
            </tr>
            <tr>
              <td className={styles.indent}>Prive opnames (netto)</td>
              <td className={styles.alignRight}>{formatBedrag(data.kapitaal.prive_opnames_netto)}</td>
            </tr>
            {data.kapitaal.or_dotatie > 0 && (
              <tr>
                <td className={styles.indent}>Dotatie oudedagreserve</td>
                <td className={styles.alignRight}>{formatBedrag(data.kapitaal.or_dotatie)}</td>
              </tr>
            )}
            <tr>
              <td></td>
              <td className={styles.alignRight}>========</td>
            </tr>
            <tr>
              <td className={styles.indent}><strong>Eigen vermogen 31-12-{data.jaar}</strong></td>
              <td className={styles.alignRight}><strong>{formatBedrag(data.kapitaal.eigen_vermogen)}</strong></td>
            </tr>
            <tr><td></td><td></td></tr>
            <tr>
              <td className={styles.indent}>Oudedagsreserve</td>
              <td className={styles.alignRight}>{formatBedrag(data.kapitaal.or_eind)}</td>
            </tr>
            <tr>
              <td></td>
              <td className={styles.alignRight}>========</td>
            </tr>
            <tr>
              <td className={styles.indent}><strong>Ondernemingsvermogen 31-12-{data.jaar}</strong></td>
              <td className={styles.alignRight}><strong>{formatBedrag(data.kapitaal.ondernemingsvermogen)}</strong></td>
            </tr>
          </tbody>
        </table>

        <div className={styles.pageFooter}>
          <span>Jaarrekening {data.bedrijf.naam} {data.jaar}</span>
          <span>Pagina 9 van 11</span>
        </div>
      </div>

      {/* Pagina 10: Exploitatierekening uitgebreid - EXACT Morris */}
      <div className={styles.page}>
        <div className={styles.sectionHeaderBar}>Explotatie rekening uitgebreid {data.jaar}</div>

        <table className={styles.jrTable} style={{marginTop: '1.5rem'}}>
          <tbody>
            <tr>
              <td className={styles.indent}>Verkoop</td>
              <td className={styles.alignRight}></td>
              <td className={styles.alignRight}>{formatBedrag(omzetTotaal)}</td>
            </tr>
            {data.exploitatie.kosten.map(kost => (
              <tr key={kost.naam}>
                <td className={styles.indent}>{kost.naam}</td>
                <td className={styles.alignRight}>{formatBedrag(kost.totaal)}</td>
                <td className={styles.alignRight}></td>
              </tr>
            ))}
            <tr>
              <td></td>
              <td className={styles.alignRight}>========</td>
              <td className={styles.alignRight}></td>
            </tr>
            <tr>
              <td></td>
              <td className={styles.alignRight}>{formatBedrag(kostenTotaal)}</td>
              <td className={styles.alignRight}></td>
            </tr>
            <tr>
              <td className={styles.indent}>WINST</td>
              <td className={styles.alignRight}>{formatBedrag(winstTotaal)}</td>
              <td className={styles.alignRight}></td>
            </tr>
            <tr>
              <td></td>
              <td className={styles.alignRight}>========</td>
              <td className={styles.alignRight}>========</td>
            </tr>
            <tr>
              <td></td>
              <td className={styles.alignRight}>{formatBedrag(omzetTotaal)}</td>
              <td className={styles.alignRight}>{formatBedrag(omzetTotaal)}</td>
            </tr>
          </tbody>
        </table>

        <div className={styles.pageFooter}>
          <span>Jaarrekening {data.bedrijf.naam} {data.jaar}</span>
          <span>Pagina 10 van 11</span>
        </div>
      </div>

      {/* Pagina 11: BTW Overzicht - EXACT Morris */}
      <div className={styles.page}>
        <div className={styles.sectionHeaderBar}>Totaal BTW {data.jaar}</div>

        <table className={styles.jrTable} style={{marginTop: '1.5rem', fontSize: '9pt'}}>
          <thead>
            <tr>
              <td style={{width: '50%'}}></td>
              <td className={styles.alignRight} style={{width: '15%'}}><strong>Omzet</strong></td>
              <td className={styles.alignRight} style={{width: '15%'}}><strong>BTW</strong></td>
            </tr>
          </thead>
          <tbody>
            {data.btw.kwartalen.map(q => (
              <tr key={q.kwartaal}>
                <td>Q{q.kwartaal}</td>
                <td className={styles.alignRight}>{formatBedrag(q.omzet_hoog + q.omzet_laag)}</td>
                <td className={styles.alignRight}>{formatBedrag(q.btw_hoog + q.btw_laag)}</td>
              </tr>
            ))}
            <tr>
              <td></td>
              <td className={styles.alignRight}>========</td>
              <td className={styles.alignRight}>========</td>
            </tr>
            <tr>
              <td><strong>Totaal</strong></td>
              <td className={styles.alignRight}><strong>{formatBedrag(data.btw.kwartalen.reduce((sum, q) => sum + q.omzet_hoog + q.omzet_laag, 0))}</strong></td>
              <td className={styles.alignRight}><strong>{formatBedrag(data.btw.kwartalen.reduce((sum, q) => sum + q.btw_hoog + q.btw_laag, 0))}</strong></td>
            </tr>
            <tr><td></td><td></td><td></td></tr>
            <tr>
              <td>Voorbelasting totaal</td>
              <td className={styles.alignRight}></td>
              <td className={styles.alignRight}>{formatBedrag(data.btw.kwartalen.reduce((sum, q) => sum + q.voorbelasting, 0))}</td>
            </tr>
            <tr>
              <td></td>
              <td className={styles.alignRight}></td>
              <td className={styles.alignRight}>========</td>
            </tr>
            <tr>
              <td><strong>Te betalen</strong></td>
              <td className={styles.alignRight}></td>
              <td className={styles.alignRight}><strong>{formatBedrag(data.btw.totaal_te_betalen)}</strong></td>
            </tr>
          </tbody>
        </table>

        <div className={styles.pageFooter}>
          <span>Jaarrekening {data.bedrijf.naam} {data.jaar}</span>
          <span>Pagina 11 van 11</span>
        </div>
      </div>
      </div>
    </BoekhoudingLayout>
  );
};

export default Jaarrekening;
